import React, { useMemo, useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardValue } from "../types/game";
import { CardSVG } from "../cards";

// Draw stack UI state (derived from server state only)
type DrawStackUI = {
  count: number;
  type: "+2" | "+4";
  active: boolean;
};

type PenaltyAnimationPhase = 
  | "idle" | "cardImpact" | "targetIndication" | "drawCounter" | "cardDraw" | "reset";

type PenaltyAnimationState = {
  phase: PenaltyAnimationPhase;
  affectedPlayerId: string | null;
  penaltyType: "+2" | "+4" | null;
  count: number;
};

interface DiscardPileProps {
  discardPile: readonly Card[];
  currentColor: string;
  currentColorHex: string;
  isOverDiscardPile: boolean;
  draggedCardIndex: number | null;
  swipingCardIndex: number | null;
  drawStackUI: DrawStackUI | null;
  discardPileRef: React.RefObject<HTMLDivElement>;
  flyingCard: { card: Card } | null; // Track flying card to hide duplicate in discard pile
  isMobile?: boolean; // Mobile device flag
  isMyTurn?: boolean; // Whether it's the current player's turn
  penaltyAnimationState?: PenaltyAnimationState; // Animation state for penalty cards
  prefersReducedMotion?: boolean; // Accessibility: reduced motion preference
}

// Generate stable rotation for a card based on its properties
// This ensures the same card always has the same rotation
function getStableRotation(card: Card, positionInPile: number): number {
  // Create a hash from card properties and position
  // Position in pile is stable (where it appears in the full discard pile)
  const hash = `${card.color}-${card.value}-${positionInPile}`;
  let hashValue = 0;
  for (let i = 0; i < hash.length; i++) {
    hashValue = ((hashValue << 5) - hashValue) + hash.charCodeAt(i);
    hashValue = hashValue & hashValue; // Convert to 32-bit integer
  }
  // Map to rotation range -3° to +3°
  return ((hashValue % 60) / 10) - 3;
}

/**
 * Calculate stacking style for a card in the discard pile.
 * 
 * Cards are stacked with slight offsets and rotations to create depth.
 * Only the top card (highest stackIndex) is fully readable.
 * 
 * @param stackIndex - 0 = bottom (oldest visible), higher = top (newer)
 * @param totalCards - Total number of visible cards in the stack
 * @returns Object with zIndex, offsetX, and offsetY
 */
function getStackStyle(stackIndex: number, totalCards: number) {
  // Z-index: bottom cards have lower z-index, top card has highest
  // This ensures newest card always appears on top
  const zIndex = stackIndex;
  
  // Dynamic offset calculation based on card count
  // More cards = tighter stacking, fewer cards = looser stacking
  // Base offset scales with total cards to maintain visual consistency
  const baseOffsetX = Math.max(2, 4 - (totalCards * 0.1)); // Range: 2-4px
  const baseOffsetY = Math.max(1.5, 3 - (totalCards * 0.1)); // Range: 1.5-3px
  
  // Each card is offset slightly from the one below it
  // Creates a cascading effect like real cards on a table
  const offsetX = stackIndex * baseOffsetX;
  const offsetY = stackIndex * baseOffsetY;
  
  return {
    zIndex,
    offsetX,
    offsetY,
  };
}

/**
 * Fan-out style function - REMOVED (fan-out functionality disabled)
 * Kept for reference but not used
 */
function getFanStyle(index: number, totalCards: number, isMobile: boolean) {
  if (totalCards <= 1) {
    return { x: 0, y: 0, rotate: 0, scale: isMobile ? 0.48 : 0.54 };
  }
  
  // Get viewport height for clamping calculations
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800;
  // Card height at mobile scale (0.48): 420px * 0.48 = 201.6px
  // Card height at desktop scale (0.54): 420px * 0.54 = 226.8px
  const cardHeight = isMobile ? 202 : 227;
  // Reserve space: top UI (~25vh), bottom UI (~40vh), some padding
  const reservedTopSpace = viewportHeight * 0.25;
  const reservedBottomSpace = viewportHeight * 0.40;
  const availableHeight = viewportHeight - reservedTopSpace - reservedBottomSpace;
  // Maximum vertical lift: ensure cards don't go off-screen
  // Leave at least cardHeight/2 padding from top and bottom
  const maxVerticalLift = Math.max(
    cardHeight, // Minimum: at least one card height
    availableHeight - cardHeight // Maximum: available space minus card height
  );
  
  // Dynamic angle range calculation
  // More cards = wider arc, fewer cards = tighter arc
  // Range: 60° (few cards) to 100° (many cards)
  // On small screens, clamp angle range to prevent overflow
  const minAngleRange = 60;
  const maxAngleRange = isMobile && viewportHeight < 700 ? 80 : 100; // Smaller angle on small screens
  const angleRange = Math.min(
    maxAngleRange,
    minAngleRange + (totalCards - 2) * 5
  );
  
  // Dynamic radius calculation
  // More cards = larger radius to prevent overlap
  // Mobile: smaller radius due to screen constraints
  // Clamp radius based on available screen height
  // Adjusted radius to match card scale (0.48/0.54)
  const baseRadius = isMobile ? 168 : 192; // Reduced by 20% for scale 0.48/0.54
  let radius = baseRadius + Math.min(totalCards - 2, 5) * 10; // Scale up to 5 extra cards
  
  // Clamp radius on mobile to ensure cards fit on screen
  if (isMobile) {
    // Maximum radius: ensure vertical lift doesn't exceed maxVerticalLift
    // For upward fan: y = -cos(angle) * radius
    // Maximum y (most upward) occurs at angle = angleRange/2
    const maxAngleRad = (angleRange / 2) * (Math.PI / 180);
    const maxYForRadius = Math.abs(Math.cos(maxAngleRad) * radius);
    if (maxYForRadius > maxVerticalLift) {
      // Scale down radius to fit available height
      radius = maxVerticalLift / Math.abs(Math.cos(maxAngleRad));
    }
    // Also ensure radius doesn't cause horizontal overflow
    const maxXForRadius = Math.abs(Math.sin(maxAngleRad) * radius * 0.6);
    const maxHorizontalSpace = (typeof window !== "undefined" ? window.innerWidth : 400) * 0.4; // 40% of screen width
    if (maxXForRadius > maxHorizontalSpace) {
      radius = Math.min(radius, maxHorizontalSpace / (Math.abs(Math.sin(maxAngleRad)) * 0.6));
    }
  }
  
  // Calculate angle distribution
  // Cards are distributed evenly across the angle range
  // index 0 (oldest) = leftmost/upmost, index totalCards-1 (newest) = rightmost/downmost
  const mid = (totalCards - 1) / 2;
  const normalizedPosition = totalCards > 1 ? (index - mid) / mid : 0;
  const angle = normalizedPosition * (angleRange / 2);
  const angleRad = (angle * Math.PI) / 180;
  
  // Fan direction: horizontal (left-right) on desktop, upward on mobile
  if (isMobile) {
    // Mobile: fan upward (cards spread upward in an arc)
    // Newest card at bottom center, older cards fan upward
    const x = Math.sin(angleRad) * radius * 0.6; // Reduced horizontal spread
    let y = -Math.cos(angleRad) * radius; // Upward arc
    // Clamp vertical lift to ensure visibility on small screens
    y = Math.max(-maxVerticalLift, y);
    return {
      x,
      y,
      rotate: angle * 0.7, // Slight rotation for natural look
      scale: 0.48, // Mobile scale (reduced by 20% from 0.6)
    };
  } else {
    // Desktop: fan horizontally (cards spread left-right in an arc)
    // Newest card at center, older cards fan outward
    const x = Math.sin(angleRad) * radius;
    const y = -Math.cos(angleRad) * radius * 0.4; // Slight upward curve
    return {
      x,
      y,
      rotate: angle,
      scale: 0.54, // Desktop scale (reduced by 20% from 0.675)
    };
  }
}

/**
 * DiscardPile Component
 * 
 * Displays the discard pile in stacked mode only:
 * 
 * - Cards stacked with slight offsets and rotations
 * - Only top card is fully readable (opacity 1.0)
 * - Older cards progressively fade (opacity 0.6-0.9)
 * - Creates depth effect like real cards on a table
 * 
 * Fan-out functionality has been removed per user request.
 * 
 * Visual Rules:
 * - Newest card always appears on top (highest z-index)
 * - Card order is never reversed visually
 * - Z-index follows card order correctly
 */
export const DiscardPile: React.FC<DiscardPileProps> = React.memo(({
  discardPile,
  currentColor,
  currentColorHex,
  isOverDiscardPile,
  draggedCardIndex,
  swipingCardIndex,
  drawStackUI,
  discardPileRef,
  flyingCard,
  isMobile = false,
  isMyTurn = false,
  penaltyAnimationState,
  prefersReducedMotion = false,
}) => {
  // Get current theme for theme-aware styling
  const getCurrentTheme = (): string => {
    if (typeof window === "undefined") return "classic-dark";
    const root = document.documentElement;
    return root.getAttribute("data-theme") || "classic-dark";
  };
  const currentTheme = getCurrentTheme();
  const isCasualParty = currentTheme === "casual-party";

  // Fan-out UI state removed - always show stacked mode only
  const containerRef = useRef<HTMLDivElement>(null);

  // Derive visible discard cards (last 10, UI-only)
  const visibleDiscardCards = useMemo(() => {
    return discardPile.slice(-10);
  }, [discardPile]);

  // Track previous top card to detect new cards
  const previousTopCardRef = useRef<string | null>(null);
  
  // Get current top card identifier
  const topCard = visibleDiscardCards[visibleDiscardCards.length - 1];
  const topCardId = topCard 
    ? `${topCard.color}-${topCard.value}-${discardPile.length - 1}`
    : null;
  
  // Detect if top card is new
  const isNewCard = topCardId !== null && topCardId !== previousTopCardRef.current;
  
  useEffect(() => {
    // Update ref after render
    if (topCardId) {
      previousTopCardRef.current = topCardId;
    }
  }, [topCardId]);

  // Fan-out/expand functionality removed - always show stacked mode

  // Check if top card matches flying card (to hide duplicate)
  const topCardMatchesFlying = useMemo(() => {
    if (!flyingCard || visibleDiscardCards.length === 0) return false;
    const topCard = visibleDiscardCards[visibleDiscardCards.length - 1];
    return (
      topCard.color === flyingCard.card.color &&
      topCard.value === flyingCard.card.value
    );
  }, [flyingCard, visibleDiscardCards]);

  /**
   * Render cards from bottom to top (oldest to newest).
   * 
   * Card order: visibleDiscardCards[0] = oldest, visibleDiscardCards[length-1] = newest
   * Visual order: Newest card always appears on top (highest z-index)
   * 
   * This ensures correct visual hierarchy while maintaining chronological order.
   */
  const cardsToRender = useMemo(() => visibleDiscardCards.map((card, index) => {
    // Index in visible array: 0 = oldest visible, length-1 = newest (top card)
    const stackIndex = index;
    const isTopCard = index === visibleDiscardCards.length - 1;
    const cardIsNew = isTopCard && isNewCard;
    // Hide top card if it matches flying card (to prevent duplicate display)
    const shouldHide = isTopCard && topCardMatchesFlying;
    
    // Position in full discard pile (for stable rotation)
    const positionInPile = discardPile.length - visibleDiscardCards.length + index;
    
    // Calculate stack positions (fan-out removed)
    const { zIndex: stackZIndex, offsetX: stackX, offsetY: stackY } = getStackStyle(stackIndex, visibleDiscardCards.length);
    const stackRotation = getStableRotation(card, positionInPile);
    
    // Scale: Discard pile cards - Smaller to allow circular arrow to be prominent
    // Base card dimensions: 300px wide, 420px tall
    // Match hand cards scale (0.15) for consistency
    // Desktop: 0.15 (15% of original - same as hand cards)
    // Mobile: 0.15 (15% of original - same as hand cards)
    const baseScale = isMobile ? 0.45 : 0.45;
    const stackScale = isTopCard ? baseScale : Math.max(isMobile ? 0.12 : 0.12, baseScale - (stackIndex * 0.01));
    // Fan-out functionality removed - always use stacked mode
    
    // Calculate card dimensions for centering
    const CARD_WIDTH = 300;
    const CARD_HEIGHT = 420;
    const stackCardWidth = CARD_WIDTH * stackScale;
    const stackCardHeight = CARD_HEIGHT * stackScale;
    
    /**
     * Opacity calculation for stacked mode
     * 
     * Stacked: Only top card fully readable (opacity 1.0)
     *          Older cards progressively fade (opacity 0.6-0.9)
     *          Creates depth effect like real stacked cards
     */
    const stackOpacity = isTopCard 
      ? 1.0  // Top card: fully opaque and readable
      : Math.max(0.6, 1.0 - (stackIndex * 0.05)); // Older cards: fade progressively

    return {
      card,
      stackIndex,
      isTopCard,
      isNewCard: cardIsNew,
      shouldHide,
      stackZIndex,
      stackX,
      stackY,
      stackRotation,
      stackScale,
      stackOpacity,
      stackCardWidth,
      stackCardHeight,
      // Use position in pile for stable key
      key: `discard-${positionInPile}-${card.color}-${card.value}`,
    };
  }), [visibleDiscardCards, discardPile.length, isNewCard, topCardMatchesFlying, isMobile]);

  /**
   * Calculate container style for discard pile.
   * 
   * Always shows stacked mode - fan-out functionality removed
   */
  const containerStyle = useMemo(() => {
    return getDiscardPileStackStyle(isMobile);
  }, [isMobile]);

  // Check if top card is a penalty card (+2 or +4)
  const isPenaltyCard = useMemo(() => {
    if (!topCard) return false;
    return topCard.value === CardValue.DRAW2 || topCard.value === CardValue.WILD4;
  }, [topCard]);

  // ANIMATION: Card Impact phase - dim other UI elements
  const isCardImpactPhase = penaltyAnimationState?.phase === "cardImpact";
  const penaltyGlowColor = useMemo(() => {
    if (!penaltyAnimationState?.penaltyType) return null;
    // Use CSS variables for penalty colors
    return penaltyAnimationState.penaltyType === "+4" 
      ? getComputedStyle(document.documentElement).getPropertyValue("--penalty-draw4").trim() || "#ff6b35"
      : getComputedStyle(document.documentElement).getPropertyValue("--penalty-draw2").trim() || "#4a90e2";
  }, [penaltyAnimationState?.penaltyType]);

  // Fan-out/expand functionality removed

  // Calculate glow intensity based on state
  // Casual Party: soft glow/pulse when card is active (only when it's player's turn)
  const glowIntensity = useMemo(() => {
    const baseIntensity = (() => {
      if (isMyTurn) {
        return 0.8; // Soft glow on player turn (reduced from 1.0 for Casual Party)
      }
      if (drawStackUI?.active) {
        return 0.6; // Medium glow for penalty cards
      }
      return 0.2; // Very soft glow when idle
    })();
    // Casual Party: softer, more subtle glow
    return isCasualParty ? baseIntensity * 0.5 : baseIntensity;
  }, [isMyTurn, drawStackUI, isCasualParty]);

  // Calculate glow size based on state
  // Casual Party: soft pulse effect when card is active
  const glowSize = useMemo(() => {
    const baseSize = (() => {
      if (isMyTurn) {
        return isMobile ? 120 : 140; // Larger glow on player turn (enlarged for prominence)
      }
      if (drawStackUI?.active) {
        return isMobile ? 100 : 120; // Medium glow for penalty cards
      }
      return isMobile ? 80 : 100; // Base glow when idle
    })();
    // Casual Party: slightly larger, softer glow
    return isCasualParty ? Math.round(baseSize * 1.1) : baseSize;
  }, [isMyTurn, drawStackUI, isMobile, isCasualParty]);

  return (
    <div 
      ref={containerRef}
      style={{
        ...containerStyle,
        pointerEvents: "auto",
        userSelect: "none",
        position: "relative",
        overflow: "visible", // Allow fan-out cards to extend beyond container
        // Create new stacking context for cards
        isolation: "isolate",
      }}
      // Click handler removed - fan-out functionality disabled
    >
      {/* ANIMATION: Dimming overlay during card impact phase */}
      {isCardImpactPhase && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: prefersReducedMotion ? 0 : 0.5 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0.1 : 0.2, ease: "easeOut" }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 9998, // Below card but above other UI
            pointerEvents: "none",
          }}
        />
      )}

      {/* No glow effects - center area empty by default, card appears only on action */}
      {cardsToRender.map(({ 
        card, 
        stackIndex, 
        isTopCard, 
        isNewCard,
        shouldHide,
        stackZIndex, 
        stackX, 
        stackY, 
        stackRotation, 
        stackScale,
        stackOpacity,
        stackCardWidth,
        stackCardHeight,
        key 
      }) => {
        // Only animate the top card when it's newly added AND no flying card animation is running
        // If flying card is running, skip animation to prevent duplicate display
        const shouldAnimateNewCard = isTopCard && isNewCard && !topCardMatchesFlying;
        
        // Always use stacked mode - fan-out functionality removed
        // Cards are positioned at 50% center, so x/y offsets need to account for half card size
        const currentCardWidth = stackCardWidth;
        const currentCardHeight = stackCardHeight;
        const currentX = stackX - currentCardWidth / 2;
        const currentY = stackY - currentCardHeight / 2;
        const currentRotate = stackRotation;
        // Always use stackScale
        const baseScaleForState = stackScale;
        // Always use stackOpacity
        // Hide card if it matches flying card (to prevent duplicate)
        const currentOpacity = shouldHide ? 0 : stackOpacity;
        
        // Calculate animated scale (for drag effects, draw stack, and penalty cards)
        // Note: Uses drag events, not hover - works on mobile via tap/drag
        const animatedScale = (() => {
          if (isTopCard) {
            // ANIMATION: Card Impact phase - overshoot scale with color-based glow
            if (isCardImpactPhase && isPenaltyCard) {
              return prefersReducedMotion 
                ? [baseScaleForState, baseScaleForState * 1.05, baseScaleForState] // Reduced motion: subtle scale
                : [baseScaleForState, baseScaleForState * 1.1, baseScaleForState]; // Overshoot scale: 1.0 → 1.1 → 1.0
            }
            if (isOverDiscardPile && (draggedCardIndex !== null || swipingCardIndex !== null)) {
              return baseScaleForState * 1.05; // Scale up by 5% when dragging over
            }
            if (drawStackUI?.active) {
              if (drawStackUI.count >= 8) {
                return [baseScaleForState, baseScaleForState * 1.1, baseScaleForState]; // Pulse loop
              }
              if (drawStackUI.count >= 6) {
                return [baseScaleForState, baseScaleForState * 1.08, baseScaleForState]; // Shake
              }
              return baseScaleForState * 1.05; // Subtle scale
            }
            // Penalty card shake effect
            if (isPenaltyCard && isNewCard) {
              return [baseScaleForState, baseScaleForState * 1.08, baseScaleForState];
            }
          }
          return baseScaleForState;
        })();

        // Calculate shake animation for penalty cards
        const shakeAnimation = isTopCard && isPenaltyCard && isNewCard
          ? {
              x: [0, -8, 8, -8, 8, 0],
              y: [0, -4, 4, -4, 4, 0],
            }
          : { x: 0, y: 0 };
        
        /**
         * Z-index calculation: Ensures newest card always appears on top
         * 
         * Visual rule: Newest card (highest stackIndex) = highest zIndex
         * 
         * Always stacked mode: zIndex = stackIndex (0 = bottom, higher = top)
         * 
         * This maintains correct card order visually without reversing the data structure.
         * Using reasonable z-index values (0-20 range) within the stacking context.
         */
        const zIndex = stackZIndex; // Always stacked mode
        
        return (
          <motion.div
            key={key}
            ref={isTopCard ? discardPileRef : undefined}
            className="discard-card"
            // Click handler removed - fan-out functionality disabled
            initial={shouldAnimateNewCard && !topCardMatchesFlying ? { 
              scale: stackScale * 0.8, 
              opacity: 0,
              x: stackX,
              y: stackY - 30, // Start higher for more dramatic entrance
              rotate: stackRotation + (isPenaltyCard ? 5 : 0), // Slight extra rotation for penalty cards
            } : false}
            animate={{
              scale: animatedScale,
              opacity: currentOpacity, // Already handles hiding if matches flying card
              x: currentX + (shakeAnimation.x as number),
              y: currentY + (shakeAnimation.y as number),
              rotate: currentRotate,
            }}
            transition={topCardMatchesFlying ? {
              // No animation if flying card is running
              duration: 0,
            } : shouldAnimateNewCard ? {
              // Real-world motion - ease-in-out only, no bounce
              duration: 0.2, // < 250ms
              ease: "easeInOut",
            } : {
              duration: 0.2, // < 250ms, ease-in-out only
              ease: "easeInOut",
            }}
            style={{
              ...discardCardWrapperStyle,
              position: "absolute",
              // Position at container center (50%, 50%)
              // x/y transforms will offset from this center point
              left: "50%",
              top: "50%",
              // Transform origin at center for proper rotation
              transformOrigin: "center center",
              zIndex: zIndex,
              cursor: "default", // No click interaction - fan-out removed
              pointerEvents: "auto",
              // Physical shadow only - no glow effects
              boxShadow: isTopCard
                ? "0 4px 12px rgba(0,0,0,0.15)" // Real-world shadow for top card
                : "0 2px 6px rgba(0,0,0,0.1)", // Softer shadow for stacked cards
              filter: "none", // No filter effects
              willChange: "transform",
            }}
          >
            <CardSVG card={card} scale={stackScale} />
          </motion.div>
        );
      })}
      
      {/* Combo Counter Overlay - Displayed on top of discard pile */}
      {(
        <AnimatePresence>
          {drawStackUI?.active && (
            <motion.div
              key={drawStackUI.count}
              initial={{ scale: 0.6, opacity: 0, y: 20 }}
              animate={{ 
                scale: prefersReducedMotion ? 1.2 : [1.2, 1.3, 1.2], // Bounce on increment
                opacity: 1, 
                y: 0,
                x: drawStackUI.count >= 6 ? (prefersReducedMotion ? 0 : [0, -3, 3, -3, 3, 0]) : 0,
              }}
              exit={{ scale: 0.8, opacity: 0, y: 10 }}
              transition={{ 
                type: "spring", 
                stiffness: prefersReducedMotion ? 300 : 400, // Faster spring for escalation
                damping: prefersReducedMotion ? 25 : 20,
                scale: {
                  duration: prefersReducedMotion ? 0.15 : 0.2, // Quick bounce for escalation feel
                  ease: "easeOut",
                },
                x: drawStackUI.count >= 6 ? {
                  duration: prefersReducedMotion ? 0.2 : 0.3,
                  repeat: Infinity,
                  repeatType: "reverse" as const,
                } : undefined,
              }}
              style={{
                position: "absolute",
                top: isMobile ? "-20px" : "0px", // Above discard pile with closer spacing
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: isMobile ? "32px" : "40px",
                fontWeight: "600", // Medium weight
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro', system-ui, sans-serif",
                color: "#007AFF", // Single accent color
                // No text shadow - clean typography
                zIndex: 100, // Above cards
                pointerEvents: "none",
                whiteSpace: "nowrap",
                // No filter effects - clean typography
                filter: "none",
              }}
            >
              +{drawStackUI.count}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
});

DiscardPile.displayName = "DiscardPile";

// Note: discardPileStackStyle is now computed dynamically based on isMobile
// This function generates the style object
const getDiscardPileStackStyle = (isMobile: boolean): React.CSSProperties => {
  // Card dimensions: 300px x 420px
  // Desktop: 0.6 scale = 180px x 252px
  // Mobile: 0.5 scale = 150px x 210px
  // Add padding for glow and stacking offsets
  const cardWidth = isMobile ? 150 : 180;
  const cardHeight = isMobile ? 210 : 252;
  const padding = isMobile ? 35 : 45; // Extra padding for glow effect
  
  return {
    position: "relative",
    width: `${cardWidth + padding}px`,
    height: `${cardHeight + padding}px`,
    margin: "0 auto",
    // Expand container when fanned out to accommodate arc
    minWidth: `${cardWidth + padding}px`,
    minHeight: `${cardHeight + padding}px`,
    // Cards are absolutely positioned, so we don't need flex centering
    // Instead, cards will be positioned relative to container center via transforms
  };
};

const discardCardWrapperStyle: React.CSSProperties = {
  borderRadius: "16px", // More rounded for game feel
  padding: "8px", // Slightly more padding for depth
  background: "rgba(255,255,255,0.05)", // More subtle background
  backdropFilter: "blur(10px)",
  // Snappy transitions: 200ms for game feel
  transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease-out, box-shadow 0.2s ease-out",
  // Subtle border for depth
  border: "1px solid rgba(255,255,255,0.08)",
};
